import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Btn, Input, Select, Badge, Textarea, Card, Modal } from "@/components/UI";
import { Plus, Trash2, Edit3, Eye, StickyNote, CheckCircle2, CircleCheck, Clock, AlertCircle, Download, Send, Mail, RotateCcw, X } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";
import { usePagination, ListPagination } from "@/components/ListPagination";

const DEPT_LABEL = { RND: "AR-GE", SALES_ORDER: "Satış Siparişi", PURCHASING: "Satın Alma", OTHER: "Diğer" };
const DEPT_COLOR = { RND: "violet", SALES_ORDER: "emerald", PURCHASING: "amber", OTHER: "slate" };
const PRIORITY_LABEL = { HIGH: "Yüksek", NORMAL: "Normal", LOW: "Düşük" };
const PRIORITY_COLOR = { HIGH: "rose", NORMAL: "orange", LOW: "yellow" };
const STATUS_LABEL = { WAITING: "Beklemede", IN_PROGRESS: "İşleme Alındı", COMPLETED: "Tamamlandı" };
const STATUS_COLOR = { WAITING: "amber", IN_PROGRESS: "indigo", COMPLETED: "emerald" };

function DeptBadge({ dept, testid }) {
  const c = DEPT_COLOR[dept] || "slate";
  return (
    <span data-testid={testid} className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium bg-${c}-100 text-${c}-800 border border-${c}-200`}>
      {DEPT_LABEL[dept] || dept}
    </span>
  );
}
function StatusBadge({ status, testid }) {
  const c = STATUS_COLOR[status] || "slate";
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold bg-${c}-100 text-${c}-800 border border-${c}-200`}>
      {status === "COMPLETED" ? <CircleCheck size={12}/> : status === "IN_PROGRESS" ? <Clock size={12}/> : <AlertCircle size={12}/>}
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ============================================================================
// LIST PAGE
// ============================================================================
export function MeetingsPage() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: sp.get("q") || "",
    customer_id: sp.get("customer_id") || "",
    status: sp.get("status") || "",
    department: sp.get("department") || "",
    priority: sp.get("priority") || "",
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v && qs.append(k, v));
      const [r, c] = await Promise.all([
        api.get(`/meetings?${qs.toString()}`),
        api.get("/customers"),
      ]);
      setRows(Array.isArray(r) ? r : []);
      setCustomers(Array.isArray(c) ? c : []);
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Sync filters → URL for shareable links.
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && qs.set(k, v));
    setSp(qs, { replace: true });
  }, [filters, setSp]);

  const custName = (m) => {
    if (m.customer_id) {
      const c = customers.find(x => x.id === m.customer_id);
      if (c) return c.legal_name || c.name;
    }
    return m.manual_customer_name || "—";
  };

  const newMeeting = async () => {
    try {
      const m = await api.post("/meetings", {});
      nav(`/meetings/${m.id}`);
    } catch (e) { alert(e.response?.data?.detail || "Toplantı oluşturulamadı"); }
  };

  const del = async (m) => {
    if (!window.confirm(`${m.meeting_no || "Toplantı"} silinsin mi? Geçmişte görünmez olacak.`)) return;
    try { await api.del(`/meetings/${m.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || "Silinemedi"); }
  };

  const approveIt = async (m) => {
    try { await api.post(`/meetings/${m.id}/approve`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "Onaylanamadı"); }
  };
  const completeIt = async (m) => {
    try { await api.post(`/meetings/${m.id}/complete`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "Tamamlanamadı"); }
  };

  const pg = usePagination(rows, "meetings", 100);

  return (
    <div>
      <PageHeader
        title="Toplantı Notları"
        description="Müşteri toplantılarında alınan notlar · Tek onayla → ilgili birimlere uyarı"
        actions={<Btn onClick={newMeeting} data-testid="new-meeting"><Plus size={14}/> Yeni Toplantı</Btn>}
      />
      <Card className="p-3 mb-4 bg-slate-50/70 border-slate-200">
        <div className="text-[11px] font-bold uppercase text-slate-500 mb-2">Filtreler</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Input label="Ara" placeholder="Başlık / Müşteri / TN..." value={filters.q} onChange={e => setFilters(f => ({...f, q: e.target.value}))} data-testid="flt-q" />
          <Select label="Müşteri" value={filters.customer_id} onChange={e => setFilters(f => ({...f, customer_id: e.target.value}))} data-testid="flt-customer">
            <option value="">Tümü</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name || c.name}</option>)}
          </Select>
          <Select label="Durum" value={filters.status} onChange={e => setFilters(f => ({...f, status: e.target.value}))} data-testid="flt-status">
            <option value="">Tümü</option>
            <option value="WAITING">Beklemede</option>
            <option value="IN_PROGRESS">İşleme Alındı</option>
            <option value="COMPLETED">Tamamlandı</option>
          </Select>
          <Select label="İlgili Bölüm" value={filters.department} onChange={e => setFilters(f => ({...f, department: e.target.value}))} data-testid="flt-department">
            <option value="">Tümü</option>
            <option value="RND">AR-GE</option>
            <option value="SALES_ORDER">Satış Siparişi</option>
            <option value="PURCHASING">Satın Alma</option>
            <option value="OTHER">Diğer</option>
          </Select>
          <Select label="Öncelik" value={filters.priority} onChange={e => setFilters(f => ({...f, priority: e.target.value}))} data-testid="flt-priority">
            <option value="">Tümü</option>
            <option value="HIGH">Yüksek</option>
            <option value="NORMAL">Normal</option>
            <option value="LOW">Düşük</option>
          </Select>
          <div className="grid grid-cols-2 gap-1">
            <Input type="date" label="Baş." value={filters.date_from} onChange={e => setFilters(f => ({...f, date_from: e.target.value}))} data-testid="flt-date-from"/>
            <Input type="date" label="Bit." value={filters.date_to} onChange={e => setFilters(f => ({...f, date_to: e.target.value}))} data-testid="flt-date-to"/>
          </div>
        </div>
      </Card>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="meetings-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">TN No</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Müşteri</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Başlık</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Tarih</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">İlgili Bölüm</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 uppercase">Durum</th>
              <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600 uppercase">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Yükleniyor…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-slate-400">Henüz toplantı yok. "Yeni Toplantı" ile başlayın.</td></tr>
            )}
            {!loading && pg.paged.map(m => (
              <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`meeting-row-${m.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{m.meeting_no}</td>
                <td className="px-3 py-2">{custName(m)}</td>
                <td className="px-3 py-2 font-medium">{m.title || <span className="text-slate-400 italic">(Başlıksız)</span>}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{m.meeting_date}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {(m.related_departments || []).length === 0 && <span className="text-slate-400 text-xs italic">—</span>}
                    {(m.related_departments || []).map(d => <DeptBadge key={d} dept={d} testid={`row-dept-${m.id}-${d}`}/>)}
                  </div>
                </td>
                <td className="px-3 py-2"><StatusBadge status={m.status} testid={`row-status-${m.id}`}/></td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Btn size="sm" variant="secondary" onClick={() => nav(`/meetings/${m.id}`)} data-testid={`open-meeting-${m.id}`}><Eye size={14}/></Btn>
                    {m.status === "WAITING" && (
                      <Btn size="sm" onClick={() => approveIt(m)} data-testid={`approve-meeting-${m.id}`}><CheckCircle2 size={14}/> Onayla</Btn>
                    )}
                    {m.status === "IN_PROGRESS" && (
                      <Btn size="sm" onClick={() => completeIt(m)} data-testid={`complete-meeting-${m.id}`}><CircleCheck size={14}/> Tamamlandı</Btn>
                    )}
                    {m.status !== "COMPLETED" && (
                      <Btn size="sm" variant="ghost" onClick={() => del(m)} data-testid={`delete-meeting-${m.id}`}><Trash2 size={14}/></Btn>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ListPagination pagination={pg} testid="meetings-pagination"/>
    </div>
  );
}

// ============================================================================
// DETAIL PAGE (autosave)
// ============================================================================
function useDebouncedAutosave(saveFn, delay = 900) {
  const timer = useRef(null);
  const pending = useRef(null);
  const trigger = useCallback((payload) => {
    pending.current = payload;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      const p = pending.current; pending.current = null;
      if (p) await saveFn(p);
    }, delay);
  }, [saveFn, delay]);
  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current; pending.current = null;
    if (p) await saveFn(p);
  }, [saveFn]);
  return { trigger, flush };
}

export function MeetingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [saveState, setSaveState] = useState({ status: "idle", at: null, error: null });
  const [custQuery, setCustQuery] = useState("");
  const [showCustList, setShowCustList] = useState(false);
  const custBoxRef = useRef(null);
  const [sendOpen, setSendOpen] = useState(null); // null | 'telegram' | 'email'
  const [sendForm, setSendForm] = useState({ to: "", subject: "", body: "", cc: "" });
  const [sending, setSending] = useState(false);
  const [reopenBusy, setReopenBusy] = useState(false);

  const load = useCallback(async () => {
    const [m, c] = await Promise.all([api.get(`/meetings/${id}`), api.get("/customers")]);
    setDoc(m);
    setCustomers(Array.isArray(c) ? c : []);
    if (m.customer_id) {
      const lookup = (c || []).find(x => x.id === m.customer_id);
      setCustQuery(lookup?.legal_name || lookup?.name || m.manual_customer_name || "");
    } else {
      setCustQuery(m.manual_customer_name || "");
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // ---- close customer autocomplete on outside click ----
  useEffect(() => {
    const h = (e) => { if (custBoxRef.current && !custBoxRef.current.contains(e.target)) setShowCustList(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const persist = useCallback(async (payload) => {
    if (!doc) return;
    setSaveState(s => ({ ...s, status: "saving" }));
    try {
      const body = { ...payload, version: doc.version };
      const fresh = await api.patch(`/meetings/${id}`, body);
      setDoc(fresh);
      setSaveState({ status: "saved", at: new Date(), error: null });
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Kayıt başarısız";
      setSaveState({ status: "error", at: new Date(), error: msg });
      // Attempt reload on stale_version to recover.
      if (e?.response?.data?.detail?.code === "stale_version") { setTimeout(load, 400); }
    }
  }, [doc, id, load]);
  // iter95: User request — bump autosave debounce to 5s so backend doesn't
  // ping on every keystroke; timer resets while user keeps typing.
  const { trigger: schedule, flush } = useDebouncedAutosave(persist, 5000);

  // ---- customer autocomplete (must be BEFORE any conditional return to keep hook order stable) ----
  const custSuggestions = useMemo(() => {
    const q = (custQuery || "").trim().toLowerCase();
    if (!q) return [];
    return customers
      .filter(c => ((c.legal_name || c.name || "").toLowerCase().includes(q)))
      .slice(0, 8);
  }, [customers, custQuery]);

  if (!doc) return <div className="p-6 text-slate-500">Yükleniyor…</div>;

  const readOnly = doc.status === "COMPLETED" && !(user?.role === "admin" || user?.role === "manager");

  const patch = (partial) => {
    setDoc(d => ({ ...d, ...partial }));
    schedule(partial);
  };

  // ---- notes editing ----
  const activeNotes = (doc.notes || []).filter(n => !n.deleted_at);
  const setNotes = (nextActive) => {
    // Preserve deleted rows so backend keeps their audit — we only send active list;
    // backend soft-deletes anything missing.
    patch({ notes: nextActive });
  };
  const addNote = () => {
    const nid = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `n-${Date.now()}`;
    const next = [...activeNotes, {
      id: nid, department: "OTHER", priority: "NORMAL", content: "", display_order: activeNotes.length,
    }];
    setNotes(next);
  };
  const editNote = (nid, patchN) => {
    const next = activeNotes.map(n => n.id === nid ? { ...n, ...patchN } : n);
    setNotes(next);
  };
  const removeNote = (nid) => {
    const next = activeNotes.filter(n => n.id !== nid);
    setNotes(next);
  };
  const onCustText = (v) => {
    setCustQuery(v);
    // free text — link nothing, keep manual name in sync
    patch({ customer_id: null, manual_customer_name: v });
    setShowCustList(true);
  };
  const pickCustomer = (c) => {
    setCustQuery(c.legal_name || c.name || "");
    patch({ customer_id: c.id, manual_customer_name: c.legal_name || c.name || "" });
    setShowCustList(false);
  };

  const doApprove = async () => {
    await flush();
    try { await api.post(`/meetings/${id}/approve`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "Onaylanamadı"); }
  };
  const doComplete = async () => {
    await flush();
    try { await api.post(`/meetings/${id}/complete`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "Tamamlanamadı"); }
  };
  const doReopen = async () => {
    if (!window.confirm("Bu tamamlanmış toplantı 'İşleme Alındı' durumuna geri getirilsin mi? İlgili bölümlerdeki uyarılar yeniden aktifleşecek.")) return;
    setReopenBusy(true);
    try { await api.post(`/meetings/${id}/reopen`, {}); load(); }
    catch (e) { alert(e.response?.data?.detail || "Yeniden açılamadı (sadece Sistem Yöneticisi)"); }
    finally { setReopenBusy(false); }
  };
  const downloadPdf = async () => {
    await flush();
    try {
      const blob = await api.getBlob(`/meetings/${id}/pdf`);
      openPdfPreview(blob, `Toplanti-Notlari-${doc?.meeting_no || id}.pdf`);
    } catch (e) { alert(e?.response?.data?.detail || "PDF indirilemedi"); }
  };
  const openSend = (kind) => {
    const cust = customers.find(c => c.id === doc?.customer_id);
    const defaults = kind === "email"
      ? { to: cust?.email || "", cc: "", subject: `Toplantı Notları — ${doc?.meeting_no || ""}`,
          body: `Sayın Yetkili,\n\n${doc?.meeting_date || ""} tarihli toplantımızda görüşülen konuların özetini ilişikte bulabilirsiniz.\n\nSaygılarımızla,\nMeta Pharma Kozmetik` }
      : { to: cust?.telegram_chat_id || "", cc: "", subject: "",
          body: `Toplantı Notları — ${doc?.meeting_no || ""}` };
    setSendForm(defaults); setSendOpen(kind);
  };
  const doSend = async () => {
    if (!sendOpen) return;
    await flush();
    setSending(true);
    try {
      if (sendOpen === "telegram") {
        const r = await api.post(`/meetings/${id}/send-telegram`, {
          chat_id: sendForm.to || undefined, caption: sendForm.body || undefined,
        });
        alert(`✓ Telegram'a gönderildi (chat_id: ${r.chat_id})`);
      } else {
        const ccList = (sendForm.cc || "").split(/[;,\s]+/).map(s => s.trim()).filter(Boolean);
        const r = await api.post(`/meetings/${id}/send-email`, {
          to: sendForm.to || undefined, subject: sendForm.subject || undefined,
          body: sendForm.body || undefined, cc: ccList.length ? ccList : undefined,
        });
        alert(`✓ E-posta gönderildi: ${r.to}${(r.cc || []).length ? ` (CC: ${r.cc.join(", ")})` : ""}`);
      }
      setSendOpen(null);
    } catch (e) { alert(e?.response?.data?.detail || "Gönderim başarısız"); }
    finally { setSending(false); }
  };

  const saveText = () => {
    if (saveState.status === "saving") return "Kaydediliyor…";
    if (saveState.status === "error") return `Kayıt başarısız — ${saveState.error}`;
    if (saveState.status === "saved" && saveState.at) {
      const t = saveState.at;
      return `Kaydedildi ✓  ${t.getHours().toString().padStart(2,"0")}:${t.getMinutes().toString().padStart(2,"0")}:${t.getSeconds().toString().padStart(2,"0")}`;
    }
    return "";
  };

  return (
    <div>
      <PageHeader
        title={doc.title ? doc.title : `Toplantı ${doc.meeting_no}`}
        description={`${doc.meeting_no} · Oluşturan: ${doc.created_by_name || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <StatusBadge status={doc.status} testid="meeting-status"/>
            <Btn variant="secondary" onClick={() => nav("/meetings")} data-testid="meetings-back">Listeye Dön</Btn>
            {/* [iter72 msg-Grup-A madde 50-52] Telegram/PDF/Email butonları toplantı
                oluşturulduğu andan itibaren aktif — WAITING durumu dahil. Gönderimden
                önce useDebouncedAutosave.flush() en güncel notların iletilmesini sağlar. */}
            <Btn variant="secondary" onClick={downloadPdf} data-testid="meeting-pdf"><Download size={14}/> PDF</Btn>
            <Btn variant="secondary" onClick={() => openSend("telegram")} data-testid="meeting-send-telegram"><Send size={14}/> Telegram</Btn>
            <Btn variant="secondary" onClick={() => openSend("email")} data-testid="meeting-send-email"><Mail size={14}/> E-posta</Btn>
            {doc.status === "WAITING" && (
              <Btn onClick={doApprove} data-testid="meeting-approve"><CheckCircle2 size={14}/> Onayla</Btn>
            )}
            {doc.status === "IN_PROGRESS" && (
              <Btn onClick={doComplete} data-testid="meeting-complete"><CircleCheck size={14}/> Tamamlandı</Btn>
            )}
            {doc.status === "COMPLETED" && user?.role === "admin" && (
              <Btn variant="danger" disabled={reopenBusy} onClick={doReopen} data-testid="meeting-reopen"><RotateCcw size={14}/> Yeniden İşleme Al</Btn>
            )}
          </div>
        }
      />

      {/* Save indicator (spec 21) */}
      <div className="mb-3 flex items-center justify-end">
        <span
          data-testid="save-state"
          className={`text-xs font-medium ${saveState.status === "error" ? "text-rose-600" : saveState.status === "saving" ? "text-amber-600" : "text-emerald-600"}`}
        >
          {saveText()}
        </span>
      </div>

      {/* Meeting Info */}
      <Card className="p-4 mb-4">
        <div className="text-[11px] font-bold uppercase text-slate-500 mb-3 tracking-wide">Toplantı Bilgileri</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input
            label="Toplantı Başlığı"
            value={doc.title || ""}
            disabled={readOnly}
            onChange={e => patch({ title: e.target.value })}
            data-testid="meeting-title"
            placeholder="örn. 2027 Yeni Ürünler Görüşmesi"
          />
          <div ref={custBoxRef} className="relative">
            <Input
              label="Müşteri (yazın veya seçin)"
              value={custQuery}
              disabled={readOnly}
              onChange={e => onCustText(e.target.value)}
              onFocus={() => setShowCustList(true)}
              data-testid="meeting-customer"
              placeholder="ABC Kozmetik…"
            />
            {showCustList && custSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                {custSuggestions.map(c => (
                  <div
                    key={c.id}
                    onMouseDown={e => { e.preventDefault(); pickCustomer(c); }}
                    className="px-3 py-2 text-sm hover:bg-indigo-50 cursor-pointer"
                    data-testid={`cust-suggest-${c.id}`}
                  >
                    <div className="font-medium">{c.legal_name || c.name}</div>
                    {c.code && <div className="text-xs text-slate-500">{c.code}</div>}
                  </div>
                ))}
              </div>
            )}
            {doc.customer_id && (
              <div className="mt-1 text-[10px] text-emerald-700">✓ Kayıtlı müşteriye bağlandı</div>
            )}
          </div>
          <Input
            type="date"
            label="Tarih"
            value={doc.meeting_date || ""}
            disabled={readOnly}
            onChange={e => patch({ meeting_date: e.target.value })}
            data-testid="meeting-date"
          />
        </div>
      </Card>

      {/* Notes */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-bold uppercase text-slate-500 tracking-wide flex items-center gap-2">
            <StickyNote size={14}/> Toplantı Notları
            <span className="text-slate-400 normal-case tracking-normal font-normal">({activeNotes.length} not)</span>
          </div>
          <Btn onClick={addNote} disabled={readOnly} data-testid="add-note"><Plus size={14}/> Not Ekle</Btn>
        </div>
        {activeNotes.length === 0 && (
          <div className="text-sm text-slate-400 italic p-6 text-center border border-dashed border-slate-200 rounded-md">
            Henüz not yok. "Not Ekle" ile yeni bir not oluşturun.
          </div>
        )}
        <div className="space-y-3">
          {activeNotes.map((n, i) => (
            <div key={n.id} className="border border-slate-200 rounded-md p-3 bg-slate-50/40" data-testid={`note-row-${n.id}`}>
              <div className="grid grid-cols-12 gap-2 mb-2">
                <div className="col-span-12 md:col-span-3">
                  <Select
                    label="İlgili Bölüm"
                    value={n.department}
                    disabled={readOnly}
                    onChange={e => editNote(n.id, { department: e.target.value })}
                    data-testid={`note-dept-${n.id}`}
                  >
                    <option value="RND">AR-GE</option>
                    <option value="SALES_ORDER">Satış Siparişi</option>
                    <option value="PURCHASING">Satın Alma</option>
                    <option value="OTHER">Diğer</option>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-2">
                  <Select
                    label="Öncelik"
                    value={n.priority}
                    disabled={readOnly}
                    onChange={e => editNote(n.id, { priority: e.target.value })}
                    data-testid={`note-priority-${n.id}`}
                  >
                    <option value="HIGH">Yüksek</option>
                    <option value="NORMAL">Normal</option>
                    <option value="LOW">Düşük</option>
                  </Select>
                </div>
                <div className="col-span-6 md:col-span-6 flex items-end gap-1">
                  <DeptBadge dept={n.department} />
                  <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold bg-${PRIORITY_COLOR[n.priority]}-100 text-${PRIORITY_COLOR[n.priority]}-800 border border-${PRIORITY_COLOR[n.priority]}-200`}>
                    {PRIORITY_LABEL[n.priority]}
                  </span>
                </div>
                <div className="col-span-12 md:col-span-1 flex items-end justify-end">
                  {!readOnly && (
                    <Btn size="sm" variant="ghost" onClick={() => removeNote(n.id)} data-testid={`note-delete-${n.id}`}><Trash2 size={14}/></Btn>
                  )}
                </div>
              </div>
              <Textarea
                label={`Not ${i + 1}`}
                rows={3}
                value={n.content || ""}
                disabled={readOnly}
                onChange={e => editNote(n.id, { content: e.target.value })}
                data-testid={`note-content-${n.id}`}
                placeholder="Görüşülen konuyu ve aksiyonu yazın…"
              />
            </div>
          ))}
        </div>

        {/* Bottom action bar */}
        <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500">
            {doc.status === "WAITING" && "Onaylandığında, seçilen bölümlere iş uyarısı oluşturulur."}
            {doc.status === "IN_PROGRESS" && "Toplantı işleme alındı. Bölümler uyarılıyor."}
            {doc.status === "COMPLETED" && "Toplantı tamamlandı. Uyarılar düştü."}
          </div>
          <div className="flex items-center gap-2">
            {doc.status === "WAITING" && (
              <Btn onClick={doApprove} data-testid="meeting-approve-bottom"><CheckCircle2 size={14}/> Onayla</Btn>
            )}
            {doc.status === "IN_PROGRESS" && (
              <Btn onClick={doComplete} data-testid="meeting-complete-bottom"><CircleCheck size={14}/> Tamamlandı</Btn>
            )}
          </div>
        </div>
      </Card>

      {/* Send Modal (Telegram or Email) */}
      {sendOpen && (
        <Modal open={!!sendOpen} onClose={() => !sending && setSendOpen(null)} title={sendOpen === "telegram" ? "Telegram ile Gönder" : "E-posta ile Gönder"}>
          <div className="space-y-3">
            <div className="text-xs text-slate-500">Toplantı notları PDF eki olarak gönderilir. Notlar profesyonel dile çevrilmiş olarak yer alır.</div>
            {sendOpen === "telegram" ? (
              <>
                <Input label="Chat ID" value={sendForm.to} onChange={e => setSendForm(f => ({...f, to: e.target.value}))} placeholder="Müşterinin Telegram chat_id'si" data-testid="send-telegram-chatid"/>
                <Textarea label="Açıklama (Caption)" rows={3} value={sendForm.body} onChange={e => setSendForm(f => ({...f, body: e.target.value}))} data-testid="send-telegram-caption"/>
                <div className="text-[11px] text-slate-500">Chat ID boş bırakılırsa müşterinin kayıtlı Telegram chat_id'si kullanılır.</div>
              </>
            ) : (
              <>
                <Input label="Alıcı" value={sendForm.to} onChange={e => setSendForm(f => ({...f, to: e.target.value}))} placeholder="ornek@musteri.com" data-testid="send-email-to"/>
                <Input label="CC (virgülle ayırın)" value={sendForm.cc} onChange={e => setSendForm(f => ({...f, cc: e.target.value}))} placeholder="muhasebe@sirket.com" data-testid="send-email-cc"/>
                <Input label="Konu" value={sendForm.subject} onChange={e => setSendForm(f => ({...f, subject: e.target.value}))} data-testid="send-email-subject"/>
                <Textarea label="Metin" rows={5} value={sendForm.body} onChange={e => setSendForm(f => ({...f, body: e.target.value}))} data-testid="send-email-body"/>
              </>
            )}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <Btn variant="secondary" onClick={() => setSendOpen(null)} disabled={sending} data-testid="send-cancel">İptal</Btn>
              <Btn onClick={doSend} disabled={sending} data-testid="send-confirm">
                {sending ? "Gönderiliyor…" : (sendOpen === "telegram" ? "Telegram'a Gönder" : "E-posta Gönder")}
              </Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
